import { type FC, Fragment, ReactNode, useMemo } from 'react';
import { Table, Tag, Spin, Empty } from 'antd';
import { type Customer } from 'types';
import { Link, Space, Text } from '@g2u/ui-kit';
import { TablePaginationConfig, type ColumnsType } from 'antd/es/table';

import styles from './customer-list.module.scss';

export type Action =
  | ((record: Customer) => ReactNode)
  | {
      title: string;
      action: (record: Customer) => void;
    };

type PaginationMetadata = {
  total: number;
  current_page: number;
  pageSize: number;
  onChange: TablePaginationConfig['onChange'];
};

type Props = { customers?: Customer[]; actions?: Action[]; paginationMetadata?: PaginationMetadata };

const columnsDefinition: ColumnsType<Customer> = [
  {
    title: 'Customer ID',
    dataIndex: 'ID',
    key: 'ID',
    width: 80,
  },
  {
    title: 'Type',
    dataIndex: 'type',
    render: (_, record) => (
      <>
        {record.type} <br />
        {record.is_parent && (
          <>
            Parent
            <br />
          </>
        )}
        {record.prepaid && (
          <>
            <Tag color="error">Pre-paid</Tag>
            <br />
          </>
        )}
        {record.is_geek_cover && <Tag color="processing">GeekCover</Tag>}
        {record.ow30dba && <Tag color="processing">OW30DBA</Tag>}
      </>
    ),
    width: 80,
  },
  {
    title: 'Contact / Company Name',
    dataIndex: ['name', 'full_name'],
    render: (_, record) => <span data-dd-privacy="mask">{record.name.full_name}</span>,
    width: 155,
  },
  {
    title: 'Contact Number',
    dataIndex: ['contact', 0],
    width: 130,
    render: (_, record) => (
      <div key={_}>
        {record.contact.map((number: string, _: number) => (
          <div key={_} data-dd-privacy="mask">
            {number}
            <br />
          </div>
        ))}
      </div>
    ),
  },
  {
    title: 'Home / Business Address',
    dataIndex: ['address', 'full_address'],
    render: (_, record) => <span data-dd-privacy="mask">{record.address.full_address}</span>,
  },
];

const actionsRenderer = (actions: Action[]) =>
  function Actions(_: any, record: Customer) {
    return (
      <Space.$ direction={Space.Direction.Vertical} size={Space.Size.Small}>
        {actions?.map((item, i) =>
          typeof item === 'function' ? (
            <Fragment key={i}>{item(record)}</Fragment>
          ) : (
            <Link.$
              key={item.title}
              onClick={(e) => {
                e.preventDefault();
                item.action(record);
              }}
              href="#"
              className={styles.customerList__action}
            >
              {item.title}
            </Link.$>
          ),
        )}
      </Space.$>
    );
  };

const defaultPageSize = 5;

export const CustomerList: FC<Props> = ({ customers, actions = [], paginationMetadata: meta }) => {
  const columns = useMemo(() => {
    if (actions.length == 0) return columnsDefinition;

    return (columnsDefinition as ColumnsType<Customer>).concat({
      key: 'actions',
      title: 'Actions',
      render: actionsRenderer(actions),
      width: 120,
    });
  }, [columnsDefinition, actions]);

  if (!customers) {
    return <Spin data-testid="spinner" />;
  }

  if (customers && !customers.length) {
    return (
      <Empty
        styles={{ image: { height: 60 } }}
        style={{ marginBottom: 20 }}
        description={<Text.$ type="secondary">Sorry, there are no matching results.</Text.$>}
      />
    );
  }

  const paginationProps: TablePaginationConfig | undefined =
    meta && meta.total > (meta.pageSize || defaultPageSize)
      ? {
          rootClassName: styles.customerList__pagination,
          total: meta.total,
          current: meta.current_page,
          showTotal: () => <>Total {meta.total} items</>,
          showSizeChanger: true,
          pageSize: meta.pageSize || defaultPageSize,
          pageSizeOptions: [defaultPageSize, 10, 20, 50, 100],
          onChange: typeof meta.onChange === 'function' ? meta.onChange : undefined,
          hideOnSinglePage: false,
        }
      : undefined;

  return <Table pagination={paginationProps} rowKey="ID" loading={false} columns={columns} dataSource={customers} />;
};
